import React, { useCallback, useMemo, useState } from 'react';
import { AppView, Store, UserRole, Province, StoreTier, BookingSlot, Brand } from './data/types';
import { useAuth } from './hooks/useAuth';
import { useBookings } from './hooks/useBookings';
import { useActivityLog } from './hooks/useActivityLog';
import { generateNewStoreSlots, makeStandardPositions, stores as defaultStores } from './data/stores';
import { brands as defaultBrands } from './data/brands';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { BrandDashboardPage } from './pages/BrandDashboardPage';
import { GrapevineDashboardPage } from './pages/GrapevineDashboardPage';
import { StoreTimelinePage } from './pages/StoreTimelinePage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminRequestsPage } from './pages/AdminRequestsPage';
import { CampaignCalendarPage } from './pages/CampaignCalendarPage';
import { RateCardPage } from './pages/RateCardPage';
import { BookingCalendarPage } from './pages/BookingCalendarPage';
import { WeeklyAllocationPage } from './pages/WeeklyAllocationPage';
export function App() {
  const {
    user,
    isAuthenticated,
    isAdmin,
    isBrand,
    isGrapevine,
    login,
    logout
  } = useAuth();
  const {
    brandRequests,
    sendBrandRequest,
    updateBrandRequest,
    activateRequest,
    approveRequest,
    rejectRequest,
    removeRequest,
    respondToInvitation
  } = useBookings();
  const activityLog = useActivityLog();
  const [currentView, setCurrentView] = useState<AppView>('login');
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [managedStores, setManagedStores] = useState<Store[]>(() => defaultStores.map(s => ({
    ...s,
    slots: s.slots.map(sl => ({
      ...sl
    }))
  })));
  const [customStores, setCustomStores] = useState<Store[]>([]);
  const allStores = useMemo(() => [...managedStores, ...customStores], [managedStores, customStores]);
  const selectedStore = useMemo(() => selectedStoreId ? allStores.find(s => s.id === selectedStoreId) || null : null, [selectedStoreId, allStores]);
  // Global slot state updater — persists across navigation
  const updateStoreSlot = useCallback((storeId: string, slotId: string, updates: Partial<BookingSlot>) => {
    setManagedStores(prev => prev.map(store => store.id === storeId ? {
      ...store,
      slots: store.slots.map(slot => slot.id === slotId ? {
        ...slot,
        ...updates
      } : slot)
    } : store));
    // Also check custom stores
    setCustomStores(prev => prev.map(store => store.id === storeId ? {
      ...store,
      slots: store.slots.map(slot => slot.id === slotId ? {
        ...slot,
        ...updates
      } : slot)
    } : store));
  }, []);
  const handleAddStore = useCallback((storeData: {
    name: string;
    location: string;
    province: Province;
    tier: StoreTier;
  }) => {
    const storeId = `store-custom-${Date.now()}`;
    const newStore: Store = {
      id: storeId,
      name: storeData.name,
      location: storeData.location,
      province: storeData.province,
      tier: storeData.tier,
      positions: makeStandardPositions(storeId),
      slots: generateNewStoreSlots(storeId, storeData.tier)
    };
    setCustomStores(prev => [...prev, newStore]);
  }, []);
  const handleLogin = useCallback((email: string, password: string, role: UserRole, brandName?: string, brandId?: string) => {
    const success = login(email, password, role, brandName, brandId);
    if (success) {
      if (role === 'admin') setCurrentView('admin-dashboard');else if (role === 'grapevine') setCurrentView('grapevine-dashboard');else setCurrentView('brand-dashboard');
    }
  }, [login]);
  const handleLogout = useCallback(() => {
    logout();
    setCurrentView('login');
    setSelectedStoreId(null);
  }, [logout]);
  const handleStoreSelect = useCallback((store: Store) => {
    setSelectedStoreId(store.id);
    setCurrentView('store-timeline');
  }, []);
  const handleBackToDashboard = useCallback(() => {
    setSelectedStoreId(null);
    if (isAdmin) setCurrentView('admin-dashboard');else if (isGrapevine) setCurrentView('grapevine-dashboard');else setCurrentView('brand-dashboard');
  }, [isAdmin, isGrapevine]);
  const handleNavigate = useCallback((view: AppView) => {
    setCurrentView(view);
  }, []);
  // Wrapped action handlers that also log to activity feed + email
  const handleApproveRequest = useCallback((requestId: string, approverName: string, approverEmail: string, conditions?: string) => {
    const req = brandRequests.find(r => r.id === requestId);
    approveRequest(requestId, approverName, approverEmail, conditions);
    if (req) {
      activityLog.addLog({
        type: 'request',
        message: `${req.brandName} booking ${conditions ? 'approved with conditions' : 'approved'}`,
        detail: `${req.campaignName || 'Campaign'} · Week ${req.weekNumber}`
      });
      // Email notification to brand contact
      if (req.contactEmail) {
        activityLog.addEmailLog(req.contactEmail, `Woolworths Booking ${conditions ? 'Approved with Conditions' : 'Approved'} — ${req.campaignName || 'Campaign'}`, `Approval confirmation sent to ${req.brandName}`, `${req.campaignName || 'Campaign'} · Week ${req.weekNumber} · ${req.stores.map(s => s.storeName).join(', ')}`);
      }
      // CC: Grapevine — lead notification
      activityLog.addEmailLog('hello@grapevine.co.za', `[CC] Booking Approved — ${req.brandName} · ${req.campaignName || 'Campaign'}`, `Grapevine notified: ${req.brandName} booking approved`, `${req.campaignName || 'Campaign'} · Week ${req.weekNumber} · ${req.stores.map(s => s.storeName).join(', ')}`);
    }
  }, [approveRequest, brandRequests, activityLog]);
  const handleRejectRequest = useCallback((requestId: string, approverName: string) => {
    const req = brandRequests.find(r => r.id === requestId);
    rejectRequest(requestId, approverName);
    if (req) {
      activityLog.addLog({
        type: 'request',
        message: `${req.brandName} booking request declined`,
        detail: `${req.campaignName || 'Campaign'} · Week ${req.weekNumber}`
      });
      // Email notification to brand contact
      if (req.contactEmail) {
        activityLog.addEmailLog(req.contactEmail, `Woolworths Booking Update — ${req.campaignName || 'Campaign'}`, `Decline notification sent to ${req.brandName}`, `${req.campaignName || 'Campaign'} · Week ${req.weekNumber}`);
      }
      // CC: Grapevine — lead notification
      activityLog.addEmailLog('hello@grapevine.co.za', `[CC] Booking Rejected — ${req.brandName} · ${req.campaignName || 'Campaign'}`, `Grapevine notified: ${req.brandName} booking declined`, `${req.campaignName || 'Campaign'} · Week ${req.weekNumber}`);
    }
  }, [rejectRequest, brandRequests, activityLog]);
  const handleRespondToInvitation = useCallback((requestId: string, response: 'accepted' | 'rejected', message?: string) => {
    const req = brandRequests.find(r => r.id === requestId);
    respondToInvitation(requestId, response, message);
    if (req) {
      activityLog.addLog({
        type: 'request',
        message: `${req.brandName} ${response === 'accepted' ? 'accepted invitation — awaiting Woolworths approval' : 'declined invitation'}`,
        detail: `${req.campaignName || 'Campaign'} · Week ${req.weekNumber}`
      });
      // Email notification to Woolworths admin
      activityLog.addEmailLog('media@woolworths.co.za', `Brand Response — ${req.brandName} ${response === 'accepted' ? 'Accepted' : 'Declined'} · ${req.campaignName || 'Campaign'}`, `${req.brandName} ${response === 'accepted' ? 'accepted' : 'declined'} the invitation`, `${req.campaignName || 'Campaign'} · Week ${req.weekNumber}`);
      // CC: Grapevine — lead notification
      activityLog.addEmailLog('hello@grapevine.co.za', `[CC] Brand Response — ${req.brandName} ${response === 'accepted' ? 'Accepted' : 'Declined'} · ${req.campaignName || 'Campaign'}`, `Grapevine notified: ${req.brandName} ${response === 'accepted' ? 'accepted' : 'declined'} invitation`, `${req.campaignName || 'Campaign'} · Week ${req.weekNumber}`);
    }
  }, [respondToInvitation, brandRequests, activityLog]);
  const handleSendBrandRequest = useCallback((requestData: any, sendNow?: boolean) => {
    sendBrandRequest(requestData, sendNow);
    activityLog.addLog({
      type: 'request',
      message: sendNow ? `New booking request sent to ${requestData.brandName}` : `Draft request created for ${requestData.brandName}`,
      detail: `${requestData.campaignName || 'Campaign'} · Week ${requestData.weekNumber}`
    });
    // Email notification when sending (not drafting)
    if (sendNow && requestData.contactEmail) {
      activityLog.addEmailLog(requestData.contactEmail, `Woolworths Media Booking Invitation — ${requestData.campaignName || 'Campaign'}`, `Booking invitation sent to ${requestData.brandName}`, `${requestData.campaignName || 'Campaign'} · Week ${requestData.weekNumber} · ${requestData.stores?.map((s: any) => s.storeName).join(', ') || ''}`);
    }
    // CC: Grapevine — lead notification (for both drafts and sends)
    if (sendNow) {
      activityLog.addEmailLog('hello@grapevine.co.za', `[CC] New Booking Request Sent — ${requestData.brandName} · ${requestData.campaignName || 'Campaign'}`, `Grapevine notified: booking request sent to ${requestData.brandName}`, `${requestData.campaignName || 'Campaign'} · Week ${requestData.weekNumber} · ${requestData.stores?.map((s: any) => s.storeName).join(', ') || ''}`);
    }
  }, [sendBrandRequest, activityLog]);
  const handleActivateRequest = useCallback((requestId: string) => {
    const req = brandRequests.find(r => r.id === requestId);
    activateRequest(requestId);
    if (req) {
      activityLog.addLog({
        type: 'request',
        message: `Booking invitation sent to ${req.brandName}`,
        detail: `${req.campaignName || 'Campaign'} · Week ${req.weekNumber}`
      });
      // Email to brand
      if (req.contactEmail) {
        activityLog.addEmailLog(req.contactEmail, `Woolworths Media Booking Invitation — ${req.campaignName || 'Campaign'}`, `Booking invitation sent to ${req.brandName}`, `${req.campaignName || 'Campaign'} · Week ${req.weekNumber} · ${req.stores.map(s => s.storeName).join(', ')}`);
      }
      // CC: Grapevine — lead notification
      activityLog.addEmailLog('hello@grapevine.co.za', `[CC] Invitation Sent — ${req.brandName} · ${req.campaignName || 'Campaign'}`, `Grapevine notified: invitation sent to ${req.brandName}`, `${req.campaignName || 'Campaign'} · Week ${req.weekNumber} · ${req.stores.map(s => s.storeName).join(', ')}`);
    }
  }, [activateRequest, brandRequests, activityLog]);
  const handleRemoveRequest = useCallback((requestId: string) => {
    const req = brandRequests.find(r => r.id === requestId);
    removeRequest(requestId);
    if (req) {
      activityLog.addLog({
        type: 'request',
        message: `Booking request for ${req.brandName} removed`,
        detail: `${req.campaignName || 'Campaign'} · Week ${req.weekNumber}`
      });
      // CC: Grapevine — lead notification
      activityLog.addEmailLog('hello@grapevine.co.za', `[CC] Request Removed — ${req.brandName} · ${req.campaignName || 'Campaign'}`, `Grapevine notified: ${req.brandName} request removed`, `${req.campaignName || 'Campaign'} · Week ${req.weekNumber}`);
    }
  }, [removeRequest, brandRequests, activityLog]);
  // Render login if not authenticated
  if (!isAuthenticated || currentView === 'login') {
    return <LoginPage onLogin={handleLogin} brands={defaultBrands} data-id="element-0" />;
  }
  // Render store timeline
  if (currentView === 'store-timeline' && selectedStore && user) {
    return <StoreTimelinePage user={user} store={selectedStore} allStores={allStores} brandRequests={brandRequests} onLogout={handleLogout} onBack={handleBackToDashboard} onNavigate={handleNavigate} onUpdateSlot={updateStoreSlot} data-id="element-1" />;
  }
  // Render campaign calendar (accessible by admin, grapevine, and brand)
  if (currentView === 'campaign-calendar' && user && (isAdmin || isGrapevine || isBrand)) {
    const draftCount = brandRequests.filter(r => r.status === 'draft').length;
    return <CampaignCalendarPage user={user} stores={allStores} brandRequests={brandRequests} draftCount={draftCount} onLogout={handleLogout} onNavigate={handleNavigate} activityLog={activityLog} brands={defaultBrands} data-id="element-2" />;
  }
  // Render rate cards
  if (currentView === 'rate-cards' && user && (isAdmin || isGrapevine)) {
    return <RateCardPage user={user} onLogout={handleLogout} onNavigate={handleNavigate} activityLog={activityLog} stores={allStores} brands={defaultBrands} brandRequests={brandRequests} readOnly={!isGrapevine} data-id="element-3" />;
  }
  // Render admin requests page (accessible by both Admin and Grapevine)
  if (currentView === 'admin-requests' && user && (isAdmin || isGrapevine)) {
    return <AdminRequestsPage user={user} requests={brandRequests} stores={allStores} onLogout={handleLogout} onBack={() => handleNavigate('admin-dashboard')} onActivateRequest={handleActivateRequest} onSendRequest={handleSendBrandRequest} onUpdateRequest={updateBrandRequest} onApprove={handleApproveRequest} onReject={handleRejectRequest} onRemoveRequest={handleRemoveRequest} activityLog={activityLog} brands={defaultBrands} data-id="element-4" />;
  }
  // Render weekly allocation report (admin and grapevine)
  if (currentView === 'weekly-allocation' && user && isGrapevine) {
    return <WeeklyAllocationPage user={user} stores={allStores} onLogout={handleLogout} onNavigate={handleNavigate} activityLog={activityLog} data-id="element-5" />;
  }
  // Render booking calendar (dedicated full-page view for admin and grapevine)
  if (currentView === 'booking-calendar' && user && (isAdmin || isGrapevine)) {
    return <BookingCalendarPage user={user} stores={allStores} brandRequests={brandRequests} onLogout={handleLogout} onNavigate={handleNavigate} activityLog={activityLog} brands={defaultBrands} onSelectStore={handleStoreSelect} data-id="element-6" />;
  }
  // Render admin dashboard
  if (currentView === 'admin-dashboard' && user && isAdmin) {
    return <AdminDashboardPage user={user} stores={allStores} brandRequests={brandRequests} onLogout={handleLogout} onStoreSelect={handleStoreSelect} onNavigate={handleNavigate} onSendRequest={handleSendBrandRequest} onUpdateRequest={updateBrandRequest} onActivateRequest={handleActivateRequest} onRemoveRequest={handleRemoveRequest} onUpdateSlot={updateStoreSlot} activityLog={activityLog} brands={defaultBrands} data-id="element-7" />;
  }
  // Render grapevine dashboard
  if (currentView === 'grapevine-dashboard' && user && isGrapevine) {
    return <GrapevineDashboardPage user={user} stores={allStores} brandRequests={brandRequests} onLogout={handleLogout} onNavigate={handleNavigate} onAddStore={handleAddStore} onStoreSelect={handleStoreSelect} onSendOpportunity={handleSendBrandRequest} activityLog={activityLog} brands={defaultBrands} data-id="element-8" />;
  }
  // Render brand dashboard
  if (currentView === 'brand-dashboard' && user && isBrand) {
    return <BrandDashboardPage user={user} stores={allStores} brandRequests={brandRequests} onLogout={handleLogout} onStoreSelect={handleStoreSelect} onNavigate={handleNavigate} onRespondToInvitation={handleRespondToInvitation} onSendRequest={handleSendBrandRequest} onUpdateRequest={updateBrandRequest} onRemoveRequest={handleRemoveRequest} activityLog={activityLog} data-id="element-9" />;
  }
  // Fallback for legacy dashboard view
  if (currentView === 'dashboard' && user) {
    if (isAdmin) {
      setCurrentView('admin-dashboard');
      return null;
    } else if (isGrapevine) {
      setCurrentView('grapevine-dashboard');
      return null;
    } else {
      setCurrentView('brand-dashboard');
      return null;
    }
  }
  return <LoginPage onLogin={handleLogin} data-id="element-10" />;
}